import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { getProductionTimeline } from "@/integrations/supabase/production-timeline";
import { useAuth } from "@/components/AuthProvider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Package, Calendar, User, DollarSign, Clock, CheckCircle, MessageCircle, ChevronDown, Scissors, Palette, RotateCcw, Wrench, Sparkles, Box, Zap, Copy, Zap as ZapIcon } from "lucide-react";
import { toast } from "sonner";

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const formatDate = (dateString: string) => {
  if (!dateString) return 'N/A';
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${day}/${month}/${year}`;
};

const getStepIcon = (stepKey: string) => {
  const iconMap: { [key: string]: any } = {
    tecelao: <Zap className="w-6 h-6" />,
    corte: <Scissors className="w-6 h-6" />,
    estampa: <Palette className="w-6 h-6" />,
    costura: <RotateCcw className="w-6 h-6" />,
    oficina: <Wrench className="w-6 h-6" />,
    acabamento: <Sparkles className="w-6 h-6" />,
    pronto: <Box className="w-6 h-6" />,
  };
  return iconMap[stepKey?.toLowerCase()] || <Clock className="w-6 h-6" />;
};

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
  toast.success('Código copiado!', {
    description: `${text} copiado para a área de transferência`,
    position: 'top-center'
  });
};

export default function PedidoCompartilhado() {
  const { codigo } = useParams<{ codigo: string }>();
  const { empresa } = useAuth();
  const [timeline, setTimeline] = useState<any>(null);
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(false);

  const { data: orderData, isLoading, error } = useQuery({
    queryKey: ["pedido-compartilhado", codigo],
    queryFn: async () => {
      if (!codigo) throw new Error("Código do pedido não fornecido");

      const { data: order, error: orderError } = await supabase
        .from("atelie_orders")
        .select("*")
        .eq("code", codigo)
        .maybeSingle();

      if (orderError || !order) throw new Error("Pedido não encontrado");

      const { data: personalizations } = await supabase
        .from("order_personalizations")
        .select("*")
        .eq("order_id", order.id);

      // Tentar carregar dados da empresa (pode falhar por RLS em modo anônimo)
      let empresaData = null;
      console.log('📋 Dados do pedido:', { user_id: order.user_id, empresa_id: order.empresa_id, fields: Object.keys(order) });

      try {
        if (order.empresa_id) {
          const { data: companyData } = await supabase
            .from("empresas_publicas")
            .select("id, nome, logo_url, telefone")
            .eq("id", order.empresa_id)
            .maybeSingle();
          empresaData = companyData;
        }
      } catch (err) {
        console.warn('⚠️ Erro ao carregar empresa:', err);
      }

      return { order, personalizations: personalizations || [], empresa: empresaData };
    },
    enabled: !!codigo,
    retry: 3,
  });

  // Carregar timeline
  useEffect(() => {
    const loadTimeline = async () => {
      if (!orderData?.order?.id) {
        console.log('⚠️ Sem order ID');
        return;
      }

      try {
        console.log('⏳ Carregando timeline para:', orderData.order.id);
        const timelineData = await getProductionTimeline(orderData.order.id);
        console.log('✅ Timeline carregada no useEffect:', timelineData);
        setTimeline(timelineData);
      } catch (error) {
        console.warn('❌ Erro ao carregar timeline:', error);
        setTimeline(null);
      }
    };

    if (orderData?.order?.id) {
      loadTimeline();
    }
  }, [orderData?.order?.id, orderData]);


  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-pink-500 to-red-500 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent mx-auto mb-4"></div>
          <p className="text-white text-lg font-medium">Carregando seu pedido...</p>
        </div>
      </div>
    );
  }

  if (error || !orderData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-pink-500 to-red-500 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-0 shadow-2xl">
          <CardContent className="pt-6 text-center">
            <div className="text-5xl mb-4">😕</div>
            <p className="text-red-700 font-bold text-lg mb-2">Pedido não encontrado</p>
            <p className="text-gray-600">Verifique o código do pedido e tente novamente. Se o problema persistir, entre em contato conosco.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const order = orderData.order;
  const personalizations = orderData.personalizations || [];
  // Usar empresa do orderData (carregada publicamente) ou fallback para useAuth
  const empresaCompartilhada = orderData.empresa || empresa;


  const discountAmount = (() => {
    if (!order.discount || order.discount === 0) return 0;
    return order.discount_type === 'percentage'
      ? (order.value * order.discount) / 100
      : order.discount;
  })();

  const finalValue = order.value - discountAmount;

  const getCurrentTimelineStatus = (): string => {
    if (!timeline || !timeline.steps || timeline.steps.length === 0 || !timeline.current_step_key) {
      return 'Aguardando aprovação';
    }

    const currentStep = timeline.steps.find((s: any) => s.key === timeline.current_step_key);
    return currentStep?.label || 'Aguardando aprovação';
  };

  const progressPercentage = (() => {
    if (!timeline || !timeline.steps || timeline.steps.length === 0 || !timeline.current_step_key) {
      return 0;
    }
    const currentIndex = timeline.steps.findIndex((s: any) => s.key === timeline.current_step_key);
    console.log('📊 Calculando progresso:', { currentIndex, steps: timeline.steps.length, currentStepKey: timeline.current_step_key });
    if (currentIndex === -1) {
      return 100;
    }
    const pct = Math.round(((currentIndex + 1) / timeline.steps.length) * 100);
    console.log('✅ Progresso calculado:', pct + '%');
    return pct;
  })();

  const calculateDaysRemaining = (): number => {
    if (!order.delivery_date) return 0;
    const deliveryDate = new Date(order.delivery_date);
    const today = new Date();
    const diffTime = deliveryDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const daysRemaining = calculateDaysRemaining();

  return (
    <>
      <Helmet>
        <title>{`Acompanhe seu pedido - ${order.customer_name || 'Pedido'} | ${empresaCompartilhada?.nome || 'Ateliê Pro'}`}</title>
        <meta name="description" content={`Acompanhe seu pedido #${order.code} em tempo real. Status de produção, itens, timeline e datas de entrega.`} />
        <meta property="og:title" content={`Acompanhe seu pedido - ${order.customer_name || 'Pedido'}`} />
        <meta property="og:description" content={`Acompanhe seu pedido #${order.code} em tempo real. Status de produção, itens, timeline e datas de entrega.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:site_name" content={empresaCompartilhada?.nome || 'Ateliê Pro'} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`Acompanhe seu pedido - ${order.customer_name || 'Pedido'}`} />
        <meta name="twitter:description" content={`Acompanhe seu pedido #${order.code} em tempo real.`} />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      {/* Cabeçalho */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-red-500 text-white py-12 md:py-14 px-4 md:px-8 backdrop-blur-md" style={{ boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15), 0 8px 32px rgba(139, 92, 246, 0.2)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex items-start gap-3 md:gap-6 mb-8">
            {empresaCompartilhada?.logo_url ? (
              <div className="logo-animated flex-shrink-0">
                <img src={empresaCompartilhada.logo_url} alt={empresaCompartilhada?.nome} className="h-12 md:h-16 object-contain" />
              </div>
            ) : (
              <div className="logo-animated flex items-center justify-center flex-shrink-0 w-12 md:w-16 h-12 md:h-16">
                <Package className="w-6 md:w-8 h-6 md:h-8 text-white" />
              </div>
            )}
            <div className="flex-1 pt-1">
              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 mb-2">
                <h1 className="text-2xl md:text-4xl font-black leading-tight">{empresaCompartilhada?.nome || 'Ateliê Pro'}</h1>
                <div className="px-2.5 py-1 bg-white/20 rounded-full backdrop-blur-sm border border-white/30 w-fit">
                  <p className="text-xs font-semibold flex items-center gap-1 whitespace-nowrap">
                    <span className="w-1.5 h-1.5 bg-green-300 rounded-full animate-pulse"></span>
                    Em Tempo Real
                  </p>
                </div>
              </div>
              <p className="text-white/85 text-xs md:text-base font-medium">Acompanhamento de Pedido em Tempo Real</p>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 md:p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 cursor-pointer" onClick={() => copyToClipboard(order.code)}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-xs md:text-sm font-semibold uppercase tracking-wide mb-1">Código do Pedido</p>
                <p className="text-2xl md:text-3xl font-black text-white">{order.code}</p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  copyToClipboard(order.code);
                }}
                className="p-3 bg-white/20 rounded-lg hover:bg-white/30 transition-all duration-300 hover:scale-110"
                title="Copiar código"
              >
                <Copy className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:py-8 space-y-6 md:space-y-8">
        <style>{`
          @keyframes shimmer {
            0% { background-position: -1000px 0; }
            100% { background-position: 1000px 0; }
          }
          @keyframes borderRotate {
            0% { border-color: #9333ea; box-shadow: 0 0 10px rgba(147, 51, 234, 0.5); }
            25% { border-color: #ec4899; box-shadow: 0 0 15px rgba(236, 72, 153, 0.6); }
            50% { border-color: #ef4444; box-shadow: 0 0 10px rgba(239, 68, 68, 0.5); }
            75% { border-color: #ec4899; box-shadow: 0 0 15px rgba(236, 72, 153, 0.6); }
            100% { border-color: #9333ea; box-shadow: 0 0 10px rgba(147, 51, 234, 0.5); }
          }
          .logo-animated {
            animation: borderRotate 3s ease-in-out infinite;
            border: 2px solid #9333ea;
            border-radius: 14px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
          }
          .progress-bar-animated {
            background-image: linear-gradient(90deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.4), rgba(255, 255, 255, 0));
            background-size: 200px 100%;
            animation: shimmer 2.5s ease-in-out infinite;
          }
          @keyframes glowPulse {
            0%, 100% { box-shadow: 0 0 20px rgba(147, 51, 234, 0.3), 0 4px 6px rgba(0, 0, 0, 0.1); }
            50% { box-shadow: 0 0 40px rgba(147, 51, 234, 0.5), 0 4px 12px rgba(0, 0, 0, 0.2); }
          }
          @keyframes badgePulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.95; }
          }
          .badge-current {
            animation: badgePulse 2s ease-in-out infinite;
          }
        `}</style>

        {/* Progresso Overall */}
        <div className="bg-gradient-to-br from-white via-purple-50/20 to-white rounded-xl shadow-lg p-5 md:p-6 border-t-4 border-purple-600 overflow-hidden" style={{ boxShadow: '0 10px 30px rgba(147, 51, 234, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.5)' }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Progresso da Produção</h2>
            <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm px-3 py-1 animate-pulse">
              {progressPercentage}% Concluído
            </Badge>
          </div>
          <div className="w-full h-6 rounded-full overflow-hidden shadow-inner bg-gray-200 relative" style={{ boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.05)' }}>
            <div
              style={{
                width: `${progressPercentage}%`,
                height: '100%',
                background: 'linear-gradient(90deg, rgb(147, 51, 234) 0%, rgb(236, 72, 153) 50%, rgb(239, 68, 68) 100%)',
                transition: 'width 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                display: 'block',
                boxShadow: '0 0 15px rgba(147, 51, 234, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.3)'
              }}
            >
              {progressPercentage > 20 && (
                <div className="absolute inset-0 progress-bar-animated opacity-60"></div>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-3 font-medium">
            {timeline?.steps?.length > 0
              ? `Etapa ${timeline.steps.findIndex((s: any) => s.key === timeline.current_step_key) + 1} de ${timeline.steps.length}`
              : 'Aguardando início'}
          </p>
        </div>

        {/* Cards de Informações */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 mt-2">
          {/* Cliente */}
          <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl shadow-md p-4 md:p-6 border-l-4 border-blue-500 hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-default" style={{ boxShadow: '0 4px 15px rgba(59, 130, 246, 0.1), 0 0 0 1px rgba(219, 234, 254, 0.8)' }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-xs font-semibold text-gray-500 uppercase">Cliente</p>
            </div>
            <p className="text-sm font-bold text-gray-900 line-clamp-2">{order.customer_name || 'N/A'}</p>
          </div>

          {/* Data de Entrega */}
          <div className="bg-gradient-to-br from-white to-green-50 rounded-xl shadow-md p-4 md:p-6 border-l-4 border-green-500 hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-default" style={{ boxShadow: '0 4px 15px rgba(34, 197, 94, 0.1), 0 0 0 1px rgba(220, 252, 231, 0.8)' }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-xs font-semibold text-gray-500 uppercase">Entrega</p>
            </div>
            <p className="text-sm font-bold text-gray-900">{formatDate(order.delivery_date)}</p>
            <p className="text-xs text-green-600 font-medium mt-2">
              {daysRemaining > 0 ? `${daysRemaining} dias restantes` : 'Prazo atingido'}
            </p>
          </div>

          {/* Valor */}
          <div className="bg-gradient-to-br from-white to-purple-50 rounded-xl shadow-md p-6 border-l-4 border-purple-500 hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-default" style={{ boxShadow: '0 4px 15px rgba(168, 85, 247, 0.1), 0 0 0 1px rgba(243, 232, 255, 0.8)' }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-gradient-to-br from-purple-100 to-violet-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
              <p className="text-xs font-semibold text-gray-500 uppercase">Valor Total</p>
            </div>
            <div className="space-y-1">
              {discountAmount > 0 && (
                <>
                  <p className="text-xs text-gray-500 line-through">{formatCurrency(order.value)}</p>
                  <p className="text-xs text-red-600 font-medium">
                    Desconto ({order.discount_type === 'percentage' ? `${order.discount}%` : 'R$'}): -{formatCurrency(discountAmount)}
                  </p>
                </>
              )}
              <p className="text-sm font-bold text-gray-900">{formatCurrency(finalValue)}</p>
            </div>
          </div>

          {/* Status */}
          <div className="bg-gradient-to-br from-white to-pink-50 rounded-xl shadow-md p-6 border-l-4 border-pink-500 hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-default" style={{ boxShadow: '0 4px 15px rgba(236, 72, 153, 0.1), 0 0 0 1px rgba(252, 231, 243, 0.8)' }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-gradient-to-br from-pink-100 to-rose-100 rounded-lg">
                <Clock className="w-6 h-6 text-pink-600" />
              </div>
              <p className="text-xs font-semibold text-gray-500 uppercase">Status</p>
            </div>
            <p className="text-sm font-bold text-gray-900">{getCurrentTimelineStatus()}</p>
          </div>
        </div>


        {/* Itens do Pedido */}
        {personalizations.length > 0 && (
          <div className="bg-gradient-to-br from-white to-green-50 rounded-xl shadow-lg p-8 border-t-4 border-green-600" style={{ boxShadow: '0 10px 30px rgba(34, 197, 94, 0.12), 0 0 0 1px rgba(220, 252, 231, 0.6)' }}>
            <div className="flex items-center gap-3 mb-8 pb-4 border-b-2 border-green-200">
              <div className="p-3 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg">
                <Package className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Itens do Pedido ({personalizations.length})</h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full hidden md:table">
                <thead>
                  <tr className="bg-gradient-to-r from-green-600 via-emerald-600 to-green-600 border-b-2 border-green-700">
                    <th className="px-4 py-3 text-left text-xs font-bold text-white">PESSOA</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-white">TAMANHO</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-white">QTD</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-white">OBSERVAÇÕES</th>
                  </tr>
                </thead>
                <tbody>
                  {personalizations.map((item: any, idx: number) => (
                    <tr key={idx} className={`border-b transition-colors ${
                      idx % 2 === 0 ? 'bg-white hover:bg-green-50' : 'bg-green-50 hover:bg-green-100'
                    }`}>
                      <td className="px-4 py-4 text-sm font-semibold text-gray-900">{item.person_name || 'N/A'}</td>
                      <td className="px-4 py-4 text-sm text-gray-700">{item.size || '—'}</td>
                      <td className="px-4 py-4 text-sm text-center font-bold text-gray-900">{item.quantity || 1}</td>
                      <td className="px-4 py-4 text-sm text-gray-600 max-w-xs">{item.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Itens do Pedido */}
        {order.catalog_products && order.catalog_products.length > 0 ? (
          <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl shadow-lg overflow-hidden" style={{ boxShadow: '0 10px 30px rgba(59, 130, 246, 0.12), 0 0 0 1px rgba(219, 234, 254, 0.6)' }}>
            <div className="bg-gradient-to-r from-blue-600 via-blue-600 to-purple-600 p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                  <Package className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-lg font-bold text-white">
                  Itens do Pedido ({order.catalog_products.length})
                </h2>
              </div>
            </div>
            {/* Desktop Table */}
            <div className="overflow-x-auto hidden md:block">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-blue-300 bg-gradient-to-r from-blue-50 to-purple-50">
                    <th className="text-left p-5 text-sm font-bold text-gray-900">Descrição</th>
                    <th className="text-center p-5 text-sm font-bold text-gray-900">Qtd</th>
                    <th className="text-right p-5 text-sm font-bold text-gray-900">Valor Unit.</th>
                    <th className="text-right p-5 text-sm font-bold text-gray-900">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {order.catalog_products.map((item: any, index: number) => {
                    const subtotal = (item.quantity || 1) * (item.price || 0);
                    return (
                      <tr key={index} className={`border-b transition-all duration-200 hover:shadow-md ${
                        index % 2 === 0 ? 'bg-white hover:bg-blue-50' : 'bg-blue-25 hover:bg-blue-100/20'
                      }`}>
                        <td className="p-5 text-sm text-gray-900 font-medium">{item.name || 'Item'}</td>
                        <td className="p-5 text-sm text-center text-gray-600 font-semibold">{item.quantity || 1}</td>
                        <td className="p-5 text-sm text-right text-gray-600">{formatCurrency(item.price || 0)}</td>
                        <td className="p-5 text-sm text-right font-bold text-blue-600">{formatCurrency(subtotal)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden p-5 space-y-4">
              {order.catalog_products.map((item: any, index: number) => {
                const subtotal = (item.quantity || 1) * (item.price || 0);
                return (
                  <div key={index} className="bg-gradient-to-br from-white to-blue-50 rounded-lg p-4 border-l-4 border-blue-500 shadow-sm hover:shadow-md transition-shadow" style={{ boxShadow: '0 2px 8px rgba(59, 130, 246, 0.08)' }}>
                    <p className="font-bold text-gray-900 mb-3">{item.name || 'Item'}</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Quantidade:</span>
                        <span className="font-semibold text-gray-900">{item.quantity || 1}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Valor Unit.:</span>
                        <span className="font-semibold text-gray-900">{formatCurrency(item.price || 0)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-blue-200">
                        <span className="text-gray-700 font-semibold">Subtotal:</span>
                        <span className="font-bold text-blue-600">{formatCurrency(subtotal)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : personalizations && personalizations.length > 0 ? (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-5">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Package className="w-5 h-5" />
                Personalizações ({personalizations.length})
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Descrição</th>
                    <th className="text-center p-4 text-sm font-semibold text-gray-600">Qtd</th>
                    <th className="text-right p-4 text-sm font-semibold text-gray-600">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {personalizations.map((item: any, index: number) => (
                    <tr key={index} className="border-b border-gray-200 hover:bg-blue-50/30 transition-colors">
                      <td className="p-4 text-sm text-gray-900 font-medium">{item.descricao || item.description || 'Item'}</td>
                      <td className="p-4 text-sm text-center text-gray-600">{item.quantity || item.qtd || 1}</td>
                      <td className="p-4 text-sm text-right font-semibold text-gray-900">{formatCurrency(item.valor || item.value || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : order.description ? (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-5">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Package className="w-5 h-5" />
                Descrição do Pedido
              </h2>
            </div>
            <div className="p-5">
              <div className="space-y-3">
                {order.description.split('\n').map((line: string, index: number) =>
                  line.trim() && (
                    <div key={index} className="p-3 bg-blue-50/50 rounded-lg border border-blue-200">
                      <p className="text-sm text-gray-900 font-medium">{line.trim()}</p>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        ) : null}

        {/* Timeline de Produção */}
        {timeline && timeline.steps && timeline.steps.length > 0 && (
          <div className="bg-gradient-to-br from-white via-blue-50/30 to-white rounded-xl shadow-lg border-t-4 border-blue-600 overflow-hidden">
            <button
              onClick={() => setIsTimelineExpanded(!isTimelineExpanded)}
              className="w-full p-5 hover:bg-blue-50/80 transition-all duration-300 text-left group"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <div className="p-1.5 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                    <Clock className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="group-hover:text-blue-700 transition-colors">Timeline de Produção</span>
                </h2>
                <div className={`transition-all duration-500 p-1 rounded-lg group-hover:bg-blue-100 ${isTimelineExpanded ? 'rotate-180 text-blue-600' : 'text-gray-400 group-hover:text-blue-600'}`}>
                  <ChevronDown className="w-5 h-5" />
                </div>
              </div>
            </button>

            {isTimelineExpanded && (
              <div className="border-t border-blue-200 p-5">
                <div className="space-y-2">
              {timeline.steps.map((step: any, index: number) => {
                const isCompleted = timeline.steps.findIndex((s: any) => s.key === timeline.current_step_key) > index;
                const isCurrent = step.key === timeline.current_step_key;
                const isExpanded = expandedStep === step.key;

                return (
                  <div key={step.key} className="flex gap-3">
                    {/* Bolinha do passo */}
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => setExpandedStep(isExpanded ? null : step.key)}
                        className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-white text-sm shadow-md transition-all cursor-pointer hover:scale-105 ${
                          isCompleted
                            ? 'bg-green-500 scale-100'
                            : isCurrent
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 scale-110 shadow-lg'
                            : 'bg-gray-300'
                        }`}
                      >
                        {isCompleted ? <CheckCircle className="w-5 h-5" /> : index + 1}
                      </button>
                      {index < timeline.steps.length - 1 && (
                        <div className={`w-1 h-12 mt-2 rounded-full transition-all ${
                          isCompleted ? 'bg-green-500' : isCurrent ? 'bg-purple-600' : 'bg-gray-300'
                        }`}></div>
                      )}
                    </div>

                    {/* Conteúdo do passo */}
                    <div className="flex-1 pb-2">
                      <button
                        onClick={() => setExpandedStep(isExpanded ? null : step.key)}
                        className={`w-full text-left p-3 rounded-lg border-2 transition-all hover:shadow-md ${
                          isCurrent
                            ? 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-400 shadow-md'
                            : isCompleted
                            ? 'bg-green-50 border-green-300'
                            : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <div className={`p-2 rounded-lg ${
                                isCurrent
                                  ? 'bg-purple-200'
                                  : isCompleted
                                  ? 'bg-green-200'
                                  : 'bg-gray-200'
                              }`}>
                                <div className={`${
                                  isCurrent ? 'text-purple-700' : isCompleted ? 'text-green-700' : 'text-gray-600'
                                }`}>
                                  {getStepIcon(step.key)}
                                </div>
                              </div>
                              <p className={`font-bold text-sm ${
                                isCurrent ? 'text-purple-700' : isCompleted ? 'text-green-700' : 'text-gray-600'
                              }`}>
                                {step.label}
                              </p>
                            </div>
                            <div className="flex gap-2 mt-2">
                              {isCurrent && (
                                <span className="badge-current inline-block px-2 py-1 bg-purple-200 text-purple-700 text-xs font-semibold rounded-full">
                                  👉 Em andamento
                                </span>
                              )}
                              {isCompleted && (
                                <span className="inline-block px-2 py-1 bg-green-200 text-green-700 text-xs font-semibold rounded-full">
                                  ✓ Concluído
                                </span>
                              )}
                              {!isCompleted && !isCurrent && (
                                <span className="inline-block px-2 py-1 bg-gray-200 text-gray-700 text-xs font-semibold rounded-full">
                                  ⏳ Aguardando
                                </span>
                              )}
                            </div>
                          </div>
                          <div className={`transition-transform duration-300 flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}>
                            <ChevronDown className={`w-5 h-5 ${
                              isCurrent ? 'text-purple-600' : isCompleted ? 'text-green-600' : 'text-gray-400'
                            }`} />
                          </div>
                        </div>
                      </button>

                      {/* Conteúdo Expandido */}
                      {isExpanded && (
                        <div className={`mt-3 p-4 rounded-lg border-2 ${
                          isCurrent
                            ? 'bg-purple-100/50 border-purple-300'
                            : isCompleted
                            ? 'bg-green-100/50 border-green-300'
                            : 'bg-gray-100/50 border-gray-300'
                        }`}
                        >
                          <div className="flex items-center gap-2 pb-3 mb-3 border-b">
                            <div className={`p-2 rounded-lg ${
                              isCurrent
                                ? 'bg-purple-300'
                                : isCompleted
                                ? 'bg-green-300'
                                : 'bg-gray-300'
                            }`}>
                              <div className="text-white">
                                {getStepIcon(step.key)}
                              </div>
                            </div>
                            <h4 className={`font-bold text-sm ${
                              isCurrent ? 'text-purple-900' : isCompleted ? 'text-green-900' : 'text-gray-700'
                            }`}>
                              {step.label}
                            </h4>
                          </div>

                          <div className="space-y-3">
                            <div>
                              <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Descrição</p>
                              <p className={`text-sm ${
                                isCurrent ? 'text-purple-900' : isCompleted ? 'text-green-900' : 'text-gray-700'
                              }`}
                              >
                                {step.description || 'Etapa de produção do seu pedido.'}
                              </p>
                            </div>

                            {isCurrent && (
                              <div className="pt-2 border-t border-purple-300">
                                <p className="text-xs font-semibold text-purple-700 mb-2">📌 Status Atual</p>
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 bg-purple-600 rounded-full animate-pulse"></div>
                                  <p className="text-sm text-purple-700 font-medium">Esta etapa está em progresso</p>
                                </div>
                              </div>
                            )}

                            {isCompleted && (
                              <div className="pt-2 border-t border-green-300">
                                <p className="text-xs font-semibold text-green-700 mb-2">✨ Concluído</p>
                                <p className="text-sm text-green-700">Esta etapa foi finalizada com sucesso</p>
                              </div>
                            )}

                            {!isCompleted && !isCurrent && (
                              <div className="pt-2 border-t border-gray-300">
                                <p className="text-xs font-semibold text-gray-600 mb-2">⏳ Próximas Etapas</p>
                                <p className="text-sm text-gray-600">Esta etapa começará em breve</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Contato - CTA Premium */}
        <div className="bg-gradient-to-br from-purple-700 via-pink-600 to-red-600 rounded-xl shadow-2xl p-8 text-white relative overflow-hidden" style={{ boxShadow: '0 20px 40px rgba(147, 51, 234, 0.3), 0 0 60px rgba(236, 72, 153, 0.2)' }}>
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl -mr-20 -mt-20"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full blur-2xl -ml-16 -mb-16"></div>

          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-white/20 rounded-lg backdrop-blur-md">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold">Precisa de Ajuda?</h3>
            </div>
            <p className="text-white/95 mb-6 text-sm leading-relaxed max-w-2xl">
              Entre em contato conosco para acompanhamento detalhado do seu pedido, dúvidas sobre o status da produção ou qualquer outra informação. Estamos sempre à disposição! 🎉
            </p>

            <div className="bg-white/15 rounded-xl p-6 backdrop-blur-lg border border-white/30">
              {empresa?.telefone ? (
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <div>
                    <p className="text-white/80 text-xs md:text-sm mb-2 font-semibold uppercase tracking-wide">💬 Fale Conosco via WhatsApp</p>
                    <p className="text-xl md:text-2xl font-bold text-white break-all">{empresaCompartilhada.telefone}</p>
                  </div>
                  <a
                    href={`https://wa.me/${empresaCompartilhada.telefone?.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full md:w-auto px-6 py-3 bg-white text-green-600 font-bold rounded-lg hover:bg-gray-100 transition-all duration-300 hover:scale-105 active:scale-95 shadow-lg flex items-center justify-center gap-2 text-sm md:text-base"
                  >
                    💬 Enviar Mensagem
                  </a>
                </div>
              ) : (
                <p className="text-white text-center text-sm">
                  Entre em contato conosco para mais informações. Estamos sempre prontos para ajudar! 😊
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Rodapé - Profissional */}
        <div className="text-center py-12">
          <div className="flex justify-center gap-2 mb-4">
            <div className="px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full border border-green-300 inline-block">
              <p className="text-xs font-bold text-green-700 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                Acompanhamento em Tempo Real
              </p>
            </div>
          </div>
          <p className="text-gray-600 text-sm font-medium mb-2">✨ Seu pedido está sendo produzido com atenção especial</p>
          <p className="text-xs text-gray-500 mb-6">Atualize a página para ver as mudanças mais recentes no status</p>

          <div className="flex justify-center gap-6 mb-6 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <span>🔒 Seguro</span>
            </div>
            <div className="flex items-center gap-1">
              <span>✓ Verificado</span>
            </div>
            <div className="flex items-center gap-1">
              <span>⚡ Rápido</span>
            </div>
          </div>

          <p className="text-xs text-gray-400 border-t border-gray-200 pt-6">
            © {new Date().getFullYear()} <span className="font-semibold text-gray-600">{empresaCompartilhada?.nome || 'Ateliê Pro'}</span> — Todos os direitos reservados
          </p>
        </div>
      </div>
      </div>
    </>
  );
}
